import React, { useEffect, useState, useCallback } from "react";
import { 
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Button,
    Card,
    CardContent,
    CardActions,
    Chip,
    Drawer,
    Fab,
    CardMedia,
    Alert,
    TextField,
    IconButton
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { opportunitiesService } from "../opportunities/opportunitiesService";
import { activityService } from "../activity/activityService";
import { messagesService } from "../messages/messagesService";
import { investmentService } from "../investments/investmentService";
import { useTranslation } from "react-i18next";
import MessageIcon from "@mui/icons-material/Message";
import FinancialVerification from "../components/FinancialVerification";
import AchievementCard from "../components/Achievements";
import { notificationsService } from "../notifications/notificationsService";
import { formatCurrency } from "../capital/capitalUtils";
import AgentNotificationsWidget from "../components/AgentDashboardWidgets";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";

const Dashboard = () => {
    const navigate = useNavigate();
    const { currentUser, userProfile, updateBalance } = useAuth();
    const { t, i18n } = useTranslation();
  
    const [opportunities, setOpportunities] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshStatus, setRefreshStatus] = useState("");
    const [kycStatus, setKycStatus] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [addAmount, setAddAmount] = useState("");
    const [balanceStatus, setBalanceStatus] = useState("");
    const [totalInvestmentBalance, setTotalInvestmentBalance] = useState(0);
    const [financialVerificationOpen, setFinancialVerificationOpen] = useState(false);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMsg, setToastMsg] = useState("");
    const [notificationFilter] = useState({ onlyUnread: false, type: "", limitCount: 20 });
    const [shownNotificationIds, setShownNotificationIds] = useState([]);
    const [lastNotificationId, setLastNotificationId] = useState(null);

    const loadUserOpportunities = useCallback(async () => {
        try {
            const data = await opportunitiesService.getActiveOpportunities();
            setOpportunities(data);
        } catch (error) {
            // Error loading user opportunities
        }
    }, []);

    const loadUserTransactions = useCallback(async () => {
        try {
            const data = await activityService.getUserTransactions(currentUser?.uid);
            setTransactions(data);
        } catch (error) {
            // Error loading user transactions
        }
    }, [currentUser?.uid]);

    const loadUserInvestments = useCallback(async () => {
        try {
            // Use the improved portfolio calculation
            const portfolioData = await investmentService.calculatePortfolioValue(currentUser?.uid);
            setTotalInvestmentBalance(portfolioData.totalValue);
        } catch (error) {
            setTotalInvestmentBalance(0);
        }
    }, [currentUser?.uid]);

    const loadDashboardData = useCallback(async () => {
        try {
            setLoading(true);
            await Promise.all([
                loadUserOpportunities(),
                loadUserTransactions(),
                loadUserInvestments(),
            ]);
        } catch (error) {
            // Error loading dashboard data
        } finally {
            setLoading(false);
        }
    }, [loadUserOpportunities, loadUserTransactions, loadUserInvestments]);

    const loadKYCStatus = useCallback(async () => {
        try {
            // Check KYC status from user profile first
            if (userProfile?.kycStatus) {
                setKycStatus({
                    status: userProfile.kycStatus,
                    data: userProfile.kycData || null
                });
                return;
            }

            // If no KYC status in user profile, check if user has completed KYC
            // For now, we'll set a default status based on user profile
            const defaultStatus = userProfile?.email === "aseflc@gmail.com" ? "approved" : "pending";
            setKycStatus({
                status: defaultStatus,
                data: null
            });
        } catch (error) {
            setKycStatus({
                status: "pending",
                data: null
            });
        }
    }, [userProfile]);

    const fetchMessages = useCallback(async () => {
        try {
            const data = await messagesService.getUserMessages(currentUser?.uid);
            setMessages(data);
        } catch (error) {
            // Error fetching user messages
        }
    }, [currentUser?.uid]);

    useEffect(() => {
        if (currentUser) {
            loadDashboardData();
            loadKYCStatus();
            fetchMessages();
            notificationsService.getNotificationsForUser(currentUser.uid, notificationFilter).then(notifs => {
                if (notifs && notifs.length > 0) {
                    // Mostrar solo si no se ha mostrado antes
                    const unseen = notifs.find(n => !shownNotificationIds.includes(n.id) && !n.read);
                    if (unseen) {
                        setToastMsg(unseen.message);
                        setToastOpen(true);
                        setLastNotificationId(unseen.id);
                        setShownNotificationIds(prev => [...prev, unseen.id]);
                    }
                }
            });
        }
    }, [currentUser, notificationFilter, loadDashboardData, loadKYCStatus, fetchMessages, shownNotificationIds]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        try {
            await messagesService.sendMessage(currentUser.uid, "Support", newMessage);
            setNewMessage("");
            fetchMessages();
        } catch (error) {
            // Error sending message
        }
    };

    const getKYCStatusColor = (status) => {
        switch (status) {
        case "approved":
        case "completed":
            return "success";
        case "pending": return "warning";
        case "rejected": return "error";
        default: return "default";
        }
    };

    const getKYCStatusIcon = (status) => {
        switch (status) {
        case "approved":
        case "completed":
            return "✅";
        case "pending": return "⏳";
        case "rejected": return "❌";
        default: return "📋";
        }
    };

    const renderKYCStatus = () => {
        if (!kycStatus) return null;

        return (
            <Alert 
                severity={getKYCStatusColor(kycStatus.status)} 
                sx={{ mb: 2 }}
                action={
                    kycStatus.status === "pending" ? (
                        <Button color="inherit" size="small" onClick={() => navigate("/kyc")}>
              Complete KYC
                        </Button>
                    ) : kycStatus.status === "rejected" ? (
                        <Button color="inherit" size="small" onClick={() => navigate("/kyc")}>
              Resubmit KYC
                        </Button>
                    ) : null
                }
            >
                <Typography variant="body2">
                    {getKYCStatusIcon(kycStatus.status)} KYC Status: {kycStatus.status?.toUpperCase()}
                    {kycStatus.status === "pending" && " - Please complete your KYC verification to start investing"}
                    {kycStatus.status === "rejected" && " - Your KYC was rejected. Please resubmit with correct information"}
                    {kycStatus.status === "approved" && " - Your KYC is approved. You can now invest in opportunities"}
                </Typography>
            </Alert>
        );
    };

    const handleToastClose = () => setToastOpen(false);

    const handleToastViewNotifications = async () => {
        setToastOpen(false);
        if (lastNotificationId) {
            await notificationsService.markAsRead(lastNotificationId);
        }
        navigate("/notifications");
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                {/* Agent notifications widget for agents */}
                {userProfile?.role === "agente" && <AgentNotificationsWidget />}
                <Box
                    sx={{
                        display: { xs: "block", md: "flex" },
                        alignItems: "flex-start",
                        gap: 3,
                        width: "100%",
                    }}
                >
                    {/* Left/Main Column */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Paper sx={{ p: 4, borderRadius: 4, mb: 4, background: "#fff", boxShadow: "0 4px 24px 0 rgba(21, 82, 99, 0.10)" }}>
                            <Typography
                                variant="h3"
                                component="h1"
                                gutterBottom
                                sx={{
                                    fontWeight: 900,
                                    fontSize: { xs: "1.5rem", md: "2.2rem" },
                                    color: "#155263",
                                    mb: 2,
                                    textAlign: "left",
                                }}
                            >
                                {t("welcomeDashboard", "Welcome to Your Dashboard")}
                            </Typography>
                            <Typography variant="body1" sx={{ color: "#155263" }}>
                                {t("dashboardDescription", "Manage your investments and track your portfolio here.")}
                            </Typography>
              
                            {renderKYCStatus()}
              
                            <AchievementCard title="Your Achievements" userTier={userProfile?.tier || "Newcomer"} userBadges={userProfile?.badges || []} />
                            {refreshStatus && (
                                <Alert 
                                    severity={refreshStatus.includes("Error") ? "error" : "success"} 
                                    sx={{ mb: 2 }}
                                    onClose={() => setRefreshStatus("")}
                                >
                                    {refreshStatus}
                                </Alert>
                            )}
              
                            <Box sx={{ marginTop: 2, marginBottom: 2 }}>
                                <Typography variant="h6" sx={{ color: "#155263" }}>{t("balance")}</Typography>
                                <Typography variant="body1" sx={{ mb: 1, color: "#155263" }}>
                                    {formatCurrency(userProfile?.balance ?? 0, "EUR", i18n.language)}
                                </Typography>
                
                                {/* Investment Balance */}
                                <Typography variant="h6" sx={{ color: "#155263", mt: 2 }}>Total Investment Balance</Typography>
                                <Typography variant="body1" sx={{ mb: 1, color: "#155263" }}>
                                    {formatCurrency(totalInvestmentBalance, "EUR", i18n.language)}
                                </Typography>
                
                                <Box
                                    component="form"
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        setBalanceStatus("");
                                        const amount = parseFloat(addAmount);
                                        if (isNaN(amount) || amount <= 0) {
                                            setBalanceStatus(t("enterValidAmount", "Please enter a valid amount."));
                                            return;
                                        }
                                        try {
                                            await updateBalance(amount);
                                            setBalanceStatus(t("balanceUpdated", "Balance updated!"));
                                            setAddAmount("");
                                            // Refresh dashboard data
                                            loadDashboardData();
                                        } catch (err) {
                                            setBalanceStatus(t("balanceUpdateError", "Error updating balance."));
                                        }
                                    }}
                                    sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}
                                >
                                    <TextField
                                        type="number"
                                        placeholder="Amount"
                                        value={addAmount}
                                        onChange={(e) => setAddAmount(e.target.value)}
                                        sx={{ width: 150 }}
                                        size="small"
                                    />
                                    <Button type="submit" variant="contained" size="small">
                    Add Funds
                                    </Button>
                                </Box>
                                {balanceStatus && (
                                    <Typography variant="body2" sx={{ mt: 1, color: balanceStatus.includes("Error") ? "error.main" : "success.main" }}>
                                        {balanceStatus}
                                    </Typography>
                                )}
                            </Box>
                        </Paper>
                    </Box>
                    {/* Right/Sidebar Column */}
                    <Box sx={{ width: { xs: "100%", md: 400 }, minWidth: 320, flexShrink: 0 }}>
                        <Paper sx={{ p: 4, borderRadius: 4, mb: 3, background: "#fff", boxShadow: "0 4px 24px 0 rgba(21, 82, 99, 0.10)" }}>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, color: "#155263", borderBottom: "2px solid #DBC078", pb: 1, mb: 2 }}
                            >
                                {t("investmentOverview", "Investment Overview")}
                            </Typography>
                            {transactions.length === 0 ? (
                                <Typography>{t("noInvestmentHistory", "No investment history yet.")}</Typography>
                            ) : (
                                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                                    {transactions.map(tx => (
                                        <Box component="li" key={tx.id} sx={{ mb: 1 }}>
                                            <b>{tx.type}</b>: {formatCurrency(tx.amount, "EUR", i18n.language)} {t("on", "on")} {tx.createdAt?.toDate ? tx.createdAt.toDate().toLocaleDateString() : ""}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Paper>
                        <Paper sx={{ p: 4, borderRadius: 4, mb: 0, background: "#fff", boxShadow: "0 4px 24px 0 rgba(21, 82, 99, 0.10)" }}>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, color: "#155263", borderBottom: "2px solid #DBC078", pb: 1, mb: 2 }}
                            >
                                {t("recentActivity", "Recent Activity")}
                            </Typography>
                            {transactions.length === 0 ? (
                                <Typography>{t("noRecentActivity", "No recent activity.")}</Typography>
                            ) : (
                                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                                    {transactions.slice(0, 5).map(tx => (
                                        <Box component="li" key={tx.id} sx={{ mb: 1 }}>
                                            <b>{tx.type}</b>: {formatCurrency(tx.amount, "EUR", i18n.language)} {t("on", "on")} {tx.createdAt?.toDate ? tx.createdAt.toDate().toLocaleDateString() : ""}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Paper>
                    </Box>
                </Box>

                {/* Available Investment Opportunities - Full Width */}
                <Paper sx={{ p: 4, borderRadius: 4, mt: 4, background: "#fff", boxShadow: "0 4px 24px 0 rgba(21, 82, 99, 0.10)" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 700, color: "#155263", borderBottom: "2px solid #DBC078", pb: 1 }}
                        >
                            {t("availableOpportunities", "Available Investment Opportunities")}
                        </Typography>
                        <Button 
                            variant="outlined" 
                            color="primary"
                            onClick={() => navigate("/opportunities")}
                        >
                            {t("viewAll", "View All")}
                        </Button>
                    </Box>
          
                    {loading ? (
                        <Typography>{t("loadingOpportunities", "Loading opportunities...")}</Typography>
                    ) : opportunities.length > 0 ? (
                        <Grid container spacing={3}>
                            {opportunities.slice(0, 6).map((opportunity) => (
                                <Grid item xs={12} md={6} lg={4} key={opportunity.id}>
                                    <Card sx={{ 
                                        display: "flex",
                                        flexDirection: "column",
                                        width: "100%",
                                        height: "100%",
                                        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: "0 8px 32px 0 rgba(21, 82, 99, 0.15)",
                                        },
                                    }}>
                                        <Box sx={{ 
                                            position: "relative",
                                            height: 200,
                                            flexShrink: 0,
                                        }}>
                                            <CardMedia
                                                component="img"
                                                height="200"
                                                image={
                                                    // Handle both old (images array) and new (media array) data structures
                                                    (opportunity.media && opportunity.media.length > 0 && opportunity.media[0].type === "image") 
                                                        ? (typeof opportunity.media[0].url === "string" ? opportunity.media[0].url : "https://picsum.photos/400/200?random=1")
                                                        : (opportunity.images && opportunity.images.length > 0) 
                                                            ? (typeof opportunity.images[0] === "string" ? opportunity.images[0] : "https://picsum.photos/400/200?random=1")
                                                            : "https://picsum.photos/400/200?random=1"
                                                }
                                                alt={opportunity.title}
                                                sx={{ objectFit: "cover" }}
                                                onError={(e) => {
                                                    e.target.src = "https://picsum.photos/400/200?random=2";
                                                }}
                                            />
                                            <Chip
                                                label={`${opportunity.riskLevel} Risk`}
                                                color={opportunity.riskLevel?.toLowerCase() === "low" ? "success" : 
                                                    opportunity.riskLevel?.toLowerCase() === "medium" ? "warning" : "error"}
                                                size="small"
                                                sx={{
                                                    position: "absolute",
                                                    top: 8,
                                                    right: 8,
                                                }}
                                            />
                                        </Box>

                                        <CardContent sx={{ 
                                            flex: "1 1 auto",
                                            display: "flex",
                                            flexDirection: "column",
                                            p: 2,
                                            flexGrow: 1,
                                        }}>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: "bold",
                                                    color: "#155263",
                                                    mb: 1,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                    lineHeight: 1.25,
                                                    height: "2.5em",
                                                }}
                                            >
                                                {opportunity.title}
                                            </Typography>
                      
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary" 
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: "vertical",
                                                    lineHeight: 1.4,
                                                    height: "4.2em",
                                                    flexGrow: 1,
                                                    mb: 1,
                                                }}
                                            >
                                                {opportunity.description}
                                            </Typography>
                      
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary" 
                                                sx={{ 
                                                    mb: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    height: "1.5em",
                                                    flexShrink: 0,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                        📍 {opportunity.location}
                                            </Typography>
                      
                                            <Box sx={{ flexGrow: 1 }} />

                                            <Box sx={{ 
                                                display: "flex",
                                                gap: 0.5,
                                                mb: 1,
                                                flexWrap: "wrap",
                                                height: "24px",
                                                flexShrink: 0,
                                                "& .MuiChip-root": {
                                                    fontSize: "0.75rem",
                                                    height: 20,
                                                },
                                            }}>
                                                <Chip 
                                                    label={`${formatCurrency(opportunity.minimumInvestment, "EUR", i18n.language)} min`} 
                                                    size="small" 
                                                />
                                                <Chip 
                                                    label={`${opportunity.expectedReturn}% return`} 
                                                    size="small" 
                                                    color="primary" 
                                                />
                                                <Chip 
                                                    label={`${opportunity.duration} months`} 
                                                    size="small" 
                                                />
                                            </Box>
                      
                                            <Typography 
                                                variant="body2" 
                                                color="#155263" 
                                                sx={{ 
                                                    fontWeight: "bold",
                                                    height: "1.5em",
                                                    flexShrink: 0,
                                                }}
                                            >
                        Total: {formatCurrency(opportunity.amount, "EUR", i18n.language)}
                                            </Typography>
                                        </CardContent>
                    
                                        <CardActions sx={{ 
                                            p: 2,
                                            pt: 0,
                                            justifyContent: "space-between",
                                            flexShrink: 0,
                                        }}>
                                            <Button 
                                                size="small" 
                                                color="primary"
                                                variant="outlined"
                                                onClick={() => navigate(`/opportunity/${opportunity.id}`)}
                                                sx={{ flex: 1, mr: 1 }}
                                            >
                        Learn More
                                            </Button>
                                            <Button 
                                                size="small" 
                                                color="primary" 
                                                variant="contained"
                                                onClick={() => navigate(`/opportunity/${opportunity.id}`)}
                                                sx={{ flex: 1 }}
                                            >
                        Invest Now
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Typography>{t("noOpportunities", "No investment opportunities available at the moment.")}</Typography>
                    )}
                </Paper>
            </Container>
            {/* Floating Action Button for Messages */}
            <Fab
                color="primary"
                aria-label="messages"
                sx={{ position: "fixed", bottom: 32, right: 32, zIndex: 1300 }}
                onClick={() => setDrawerOpen(true)}
            >
                <MessageIcon />
            </Fab>
            {/* Drawer for Messages */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: { xs: "100vw", sm: 400 } } }}
            >
                <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
                    <Typography variant="h5" sx={{ mb: 2, color: "#155263", fontWeight: 700 }}>Messages</Typography>
                    <Box sx={{ flexGrow: 1, overflow: "auto", mb: 2 }}>
                        {messages.map((message, index) => (
                            <Box key={index} sx={{ mb: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>{message.sender}</Typography>
                                <Typography variant="body2">{message.text}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {message.timestamp?.toDate ? message.timestamp.toDate().toLocaleString() : ""}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box component="form" onSubmit={handleSendMessage} sx={{ display: "flex", gap: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Type a message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                        />
                        <Button type="submit" variant="contained" size="small">
              Send
                        </Button>
                    </Box>
                </Box>
            </Drawer>
      
            {/* Financial Verification Dialog */}
            <FinancialVerification
                userId={currentUser?.uid}
                open={financialVerificationOpen}
                onClose={() => setFinancialVerificationOpen(false)}
            />

            {toastOpen && (
                <Paper
                    elevation={6}
                    sx={{
                        position: "fixed",
                        bottom: 24,
                        right: 24,
                        minWidth: 320,
                        maxWidth: 400,
                        bgcolor: "#1976d2",
                        color: "#fff",
                        borderRadius: 3,
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        zIndex: 2000,
                        boxShadow: 6
                    }}
                >
                    <NotificationsIcon sx={{ fontSize: 32 }} />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1">{toastMsg}</Typography>
                        <Button color="inherit" size="small" onClick={handleToastViewNotifications}>
                            Ver notificaciones
                        </Button>
                    </Box>
                    <IconButton color="inherit" onClick={handleToastClose}>
                        <CloseIcon />
                    </IconButton>
                </Paper>
            )}

            {/* Botón "Close Account / Offboarding" oculto */}
        </Box>
    );
};

export default Dashboard; 
